// src/pages/VendorsPage.js
import React, { useState, useEffect } from 'react';
import { vendorService } from '../services/api';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import { validateWhatsAppNumber } from '../utils/phoneValidation';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';

const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    occupation: '',
    location: '',
    contactNumber: '',
    email: '',
    rating: 5,
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [phoneValidationError, setPhoneValidationError] = useState('');

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const vendorData = await vendorService.getAllVendors();
      setVendors(vendorData);
      setError('');
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setError('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneValidation = (phoneNumber) => {
    if (!phoneNumber.trim()) {
      setPhoneValidationError('');
      return;
    }
    
    const validation = validateWhatsAppNumber(phoneNumber);
    if (!validation.isValid) {
      setPhoneValidationError(validation.error);
    } else {
      setPhoneValidationError('');
    }
  };

  const handleAddVendor = () => {
    setEditingVendor(null);
    setError(''); // Clear any previous errors
    setPhoneValidationError(''); // Clear phone validation error
    setFormData({
      name: '',
      occupation: '',
      location: '',
      contactNumber: '',
      email: '',
      rating: 5,
      notes: ''
    });
    setShowAddModal(true);
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setError(''); // Clear any previous errors
    setPhoneValidationError(''); // Clear phone validation error
    setFormData({
      name: vendor.name || '',
      occupation: vendor.occupation || '',
      location: vendor.location || '',
      contactNumber: vendor.contactNumber || '',
      email: vendor.email || '',
      rating: vendor.rating || 5,
      notes: vendor.notes || ''
    });
    setShowAddModal(true);
  };

  const handleSaveVendor = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.occupation.trim()) {
      setError('Name and occupation are required');
      return;
    }

    // Check for phone validation error
    if (phoneValidationError) {
      setError(phoneValidationError);
      return;
    }

    setSaving(true);
    try {
      if (editingVendor) {
        // Update existing vendor
        const updatedVendor = await vendorService.updateVendor(editingVendor._id, formData);
        setVendors(vendors.map(v => v._id === editingVendor._id ? updatedVendor : v));
      } else {
        // Create new vendor
        const newVendor = await vendorService.createVendor(formData);
        setVendors([newVendor, ...vendors]);
      }
      
      setShowAddModal(false);
      setEditingVendor(null);
    } catch (error) {
      console.error('Error saving vendor:', error);
      setError('Failed to save vendor. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVendor = (vendorId, vendorName) => {
    const vendor = vendors.find(v => v._id === vendorId);
    setVendorToDelete(vendor);
    setShowDeleteModal(true);
  };

  const confirmDeleteVendor = async () => {
    if (!vendorToDelete) return;
    
    try {
      await vendorService.deleteVendor(vendorToDelete._id);
      setVendors(vendors.filter(v => v._id !== vendorToDelete._id));
      setShowDeleteModal(false);
      setVendorToDelete(null);
    } catch (error) {
      console.error('Error deleting vendor:', error);
      setError(error.response?.data?.message || 'Failed to delete vendor. Please try again.');
      setShowDeleteModal(false);
      setVendorToDelete(null);
    }
  };

  const cancelDeleteVendor = () => {
    setShowDeleteModal(false);
    setVendorToDelete(null);
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        i <= rating ? 
          <StarIcon key={i} style={{ color: '#ffc107', fontSize: '16px' }} /> :
          <StarBorderIcon key={i} style={{ color: '#ddd', fontSize: '16px' }} />
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="vendors-container">
        <div className="vendors-header">
          <h1>Vendors</h1>
        </div>
        <div className="loading-message">Loading vendors...</div>
      </div>
    );
  }

  return (
    <div className="vendors-container">
      <div className="vendors-header">
        <div className="header-left">
          <h1>Vendors</h1>
          <p>Manage your vendor directory</p>
        </div>
        <button className="add-vendor-btn" onClick={handleAddVendor}>
          <AddIcon className="button-icon" />
          Add New Vendor
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchVendors} style={{ marginLeft: '15px', padding: '5px 10px' }}>
            Retry
          </button>
        </div>
      )}

      <div className="vendors-grid">
        {vendors.length === 0 ? (
          <div className="empty-vendors">
            <BusinessIcon style={{ fontSize: '48px', color: '#ccc', marginBottom: '16px' }} />
            <h3>No vendors yet</h3>
            <p>Add your first vendor to get started</p>
            <button className="add-first-vendor-btn" onClick={handleAddVendor}>
              <AddIcon className="button-icon" />
              Add First Vendor
            </button>
          </div>
        ) : (
          vendors.map(vendor => (
            <div key={vendor._id} className="vendor-card">
              <div className="vendor-card-header">
                <div className="vendor-info">
                  <h3 className="vendor-name">{vendor.name}</h3>
                  <p className="vendor-occupation">{vendor.occupation}</p>
                  {vendor.rating && (
                    <div className="vendor-rating">
                      {renderStars(vendor.rating)}
                    </div>
                  )}
                </div>
                <div className="vendor-actions">
                  <button 
                    className="vendor-action-btn edit"
                    onClick={() => handleEditVendor(vendor)}
                    title="Edit Vendor"
                  >
                    <EditIcon />
                  </button>
                  <button 
                    className="vendor-action-btn delete"
                    onClick={() => handleDeleteVendor(vendor._id, vendor.name)}
                    title="Delete Vendor"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
              
              <div className="vendor-details">
                {vendor.location && (
                  <div className="vendor-detail">
                    <LocationOnIcon className="detail-icon" />
                    <span>{vendor.location}</span>
                  </div>
                )}
                {vendor.contactNumber && (
                  <div className="vendor-detail">
                    <PhoneIcon className="detail-icon" />
                    <span>{vendor.contactNumber}</span>
                  </div>
                )}
                {vendor.email && (
                  <div className="vendor-detail">
                    <EmailIcon className="detail-icon" />
                    <span>{vendor.email}</span>
                  </div>
                )}
              </div>
              
              {vendor.notes && (
                <div className="vendor-notes">
                  <p>{vendor.notes}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Vendor Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</h3>
              <button 
                className="close-button"
                onClick={() => setShowAddModal(false)}
              >
                <CloseIcon />
              </button>
            </div>
            
            <form onSubmit={handleSaveVendor} className="vendor-form">
              <div className="form-field">
                <label className="form-label">
                  <BusinessIcon className="form-icon" />
                  <span>Name *</span>
                </label>
                <div className="form-input-container">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter vendor name"
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">
                  <BusinessIcon className="form-icon" />
                  <span>Occupation *</span>
                </label>
                <div className="form-input-container">
                  <input
                    type="text"
                    value={formData.occupation}
                    onChange={(e) => setFormData({...formData, occupation: e.target.value})}
                    placeholder="e.g., Decorator, Caterer, Photographer"
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">
                  <LocationOnIcon className="form-icon" />
                  <span>Location</span>
                </label>
                <div className="form-input-container">
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Enter location"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">
                  <PhoneIcon className="form-icon" />
                  <span>Contact</span>
                </label>
                <div className="form-input-container">
                  <input
                    type="tel"
                    value={formData.contactNumber}
                    onChange={(e) => {
                      setFormData({...formData, contactNumber: e.target.value});
                      // Clear validation error when user types
                      if (phoneValidationError) {
                        setPhoneValidationError('');
                      }
                    }}
                    onBlur={(e) => handlePhoneValidation(e.target.value)}
                    placeholder="Enter contact number"
                    className={`form-input ${phoneValidationError ? 'error' : ''}`}
                  />
                  {phoneValidationError && (
                    <div className="field-error">{phoneValidationError}</div>
                  )}
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">
                  <EmailIcon className="form-icon" />
                  <span>Email</span>
                </label>
                <div className="form-input-container">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="Enter email address"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">
                  <StarIcon className="form-icon" />
                  <span>Rating</span>
                </label>
                <div className="form-input-container">
                  <select
                    value={formData.rating}
                    onChange={(e) => setFormData({...formData, rating: parseInt(e.target.value)})}
                    className="form-input"
                  >
                    <option value={5}>5 Stars - Excellent</option>
                    <option value={4}>4 Stars - Very Good</option>
                    <option value={3}>3 Stars - Good</option>
                    <option value={2}>2 Stars - Fair</option>
                    <option value={1}>1 Star - Poor</option>
                  </select>
                </div>
              </div>

              <div className="form-field">
                <label className="form-label">
                  <EditIcon className="form-icon" />
                  <span>Notes</span>
                </label>
                <div className="form-input-container">
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes about this vendor"
                    className="form-input"
                    rows={3}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => setShowAddModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="save-event-btn"
                  disabled={saving}
                >
                  {saving ? (editingVendor ? 'Updating...' : 'Adding...') : (editingVendor ? 'Update Vendor' : 'Add Vendor')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onConfirm={confirmDeleteVendor}
        onCancel={cancelDeleteVendor}
        title="Delete Vendor"
        itemName={vendorToDelete?.name}
        itemType="vendor"
        message="This vendor will be permanently removed from your database. This action cannot be undone."
      />
    </div>
  );
};

export default VendorsPage;